import { useToast } from "@/components/ui/toast"
import {
  CreatePaymentIntentBeautyInput,
  StripeTerminalReader,
  StripeTerminalReaderCreateBeautyInput,
} from "@okeys/shared-utils"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { customAxios } from "../utils/customAxios/customAxios"
import { getToken } from "../utils/functions/getToken"

// Types pour Stripe Terminal
export type StripeTerminalLocation = {
  id: string
  object: "terminal.location"
  address: {
    city: string
    country: string
    line1: string
    line2?: string
    postal_code: string
    state?: string
  }
  configuration_overrides?: string
  display_name: string
  livemode: boolean
  metadata: Record<string, string>
}

export type StripePaymentIntent = {
  id: string
  object: "payment_intent"
  amount: number
  currency: string
  status:
    | "requires_payment_method"
    | "requires_confirmation"
    | "requires_action"
    | "processing"
    | "requires_capture"
    | "canceled"
    | "succeeded"
  client_secret?: string
  metadata: Record<string, string>
}

export type ProcessPaymentInput = {
  payment_intent: string
  reader: string
}

export type CancelPaymentInput = {
  payment_intent: string
}

// ============================================================================
// HOOKS POUR L'APPLICATION DE CAISSE (Usage quotidien)
// ============================================================================

/**
 * Hook pour récupérer tous les readers disponibles pour ce salon
 * Utilisé pour choisir quel terminal utiliser pour le paiement
 */
export const useStripeReadersQuery = () => {
  const { data, isError, isLoading, error, refetch } = useQuery({
    queryKey: ["stripe-readers"],
    queryFn: async (): Promise<StripeTerminalReader[]> => {
      const token = await getToken()
      if (!token) {
        throw new Error("No token")
      }

      const response = await customAxios.get("/stripe-terminal/readers", {
        headers: {
          Authorization: token,
        },
      })

      return response.data.data as StripeTerminalReader[]
    },
    staleTime: 60000, // 1 minute
    refetchOnWindowFocus: true,
  })
  return {
    readers: data,
    isError,
    isLoading,
    error,
    refetch,
  }
}

/**
 * Hook pour créer un PaymentIntent
 * Utilisé pour chaque nouvelle transaction de paiement
 */
export const useCreatePaymentIntentMutation = () => {
  const { addToast } = useToast()

  return useMutation({
    mutationFn: async (
      paymentData: CreatePaymentIntentBeautyInput
    ): Promise<StripePaymentIntent> => {
      const token = await getToken()
      if (!token) {
        throw new Error("No token")
      }

      const response = await customAxios.post(
        "/payment/payment-intent",
        paymentData,
        {
          headers: {
            Authorization: token,
          },
        }
      )

      return response.data as StripePaymentIntent
    },
    onError: (error: Error) => {
      console.error("Erreur lors de la création du PaymentIntent:", error)
      addToast({
        type: "error",
        title: "Erreur de paiement",
        description: "Impossible de créer l'intention de paiement",
      })
    },
  })
}

/**
 * Hook pour traiter un PaymentIntent sur un terminal
 * Utilisé pour envoyer le paiement au terminal Stripe
 */
export const useProcessPaymentIntentMutation = () => {
  const { addToast } = useToast()

  return useMutation({
    mutationFn: async ({
      paymentIntentId,
      readerId,
    }: {
      paymentIntentId: string
      readerId: string
    }): Promise<{ success: boolean; message?: string }> => {
      const token = await getToken()
      if (!token) {
        throw new Error("No token")
      }

      const response = await customAxios.post(
        "/payment/payment-intent-process",
        {
          paymentIntentId,
          readerId,
        },
        {
          headers: {
            Authorization: token,
          },
        }
      )

      return response.data
    },
    onError: (error: Error) => {
      console.error("Erreur lors du traitement du PaymentIntent:", error)
      addToast({
        type: "error",
        title: "Erreur de traitement",
        description: "Impossible d'envoyer le paiement au terminal",
      })
    },
  })
}

/**
 * Hook pour annuler un PaymentIntent
 * Utilisé quand le commerçant annule une transaction en cours
 */
export const useCancelPaymentIntentMutation = () => {
  const { addToast } = useToast()

  return useMutation({
    mutationFn: async (
      paymentIntentId: string
    ): Promise<StripePaymentIntent> => {
      const token = await getToken()
      if (!token) {
        throw new Error("No token")
      }

      const response = await customAxios.post(
        `/payment/payment-intents/${paymentIntentId}/cancel`,
        {},
        {
          headers: {
            Authorization: token,
          },
        }
      )

      return response.data as StripePaymentIntent
    },
    onError: (error: Error) => {
      console.error("Erreur lors de l'annulation du PaymentIntent:", error)
      addToast({
        type: "error",
        title: "Erreur d'annulation",
        description: "Impossible d'annuler la transaction sur Stripe",
      })
    },
  })
}

/**
 * Hook pour récupérer le connection token pour Stripe Terminal SDK
 * Nécessaire pour initialiser la connexion avec les readers
 */
export type StripeConnectionTokenInfo = {
  secret: string
  locationId: string | null
  hasLocation: boolean
}

export const useConnectionTokenQuery = () => {
  return useQuery({
    queryKey: ["stripe-connection-token"],
    queryFn: async (): Promise<StripeConnectionTokenInfo> => {
      const token = await getToken()
      if (!token) {
        throw new Error("No token")
      }

      const response = await customAxios.get(
        "/stripe-terminal/connection-token",
        {
          headers: {
            Authorization: token,
          },
        }
      )

      return {
        secret: response.data.secret,
        locationId: response.data.locationId ?? null,
        hasLocation: !!response.data.hasLocation,
      }
    },
    staleTime: 60000, // 1 minute (réduit de 5 minutes pour éviter les tokens expirés)
    refetchOnWindowFocus: false,
    retry: 3, // Retry automatique en cas d'échec
  })
}

/**
 * Hook pour récupérer le statut d'un PaymentIntent
 * Utilisé pour suivre l'évolution d'une transaction en cours
 */
export const usePaymentIntentQuery = (paymentIntentId: string | null) => {
  return useQuery({
    queryKey: ["payment-intent", paymentIntentId],
    queryFn: async (): Promise<StripePaymentIntent> => {
      const token = await getToken()
      if (!token) {
        throw new Error("No token")
      }

      const response = await customAxios.get(
        `/payment/payment-intents/${paymentIntentId}`,
        {
          headers: {
            Authorization: token,
          },
        }
      )

      return response.data as StripePaymentIntent
    },
    enabled: !!paymentIntentId,
    refetchInterval: 2000, // Refetch toutes les 2 secondes pour suivre le statut
    refetchIntervalInBackground: false,
  })
}

/**
 * Hook pour supprimer un reader
 * Utilisé dans l'interface de gestion des terminaux
 */
export const useDeleteReaderMutation = () => {
  const { addToast } = useToast()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (readerId: string) => {
      const token = await getToken()
      if (!token) throw new Error("No token")

      await customAxios.delete(`/stripe-terminal/readers/${readerId}`, {
        headers: { Authorization: token },
      })
    },
    onMutate: async (readerId) => {
      // Annuler les requêtes en cours pour éviter les conflits
      await queryClient.cancelQueries({ queryKey: ["stripe-readers"] })

      // Snapshot de l'état précédent
      const previousReaders = queryClient.getQueryData<StripeTerminalReader[]>([
        "stripe-readers",
      ])

      // Optimistic update - supprimer le reader de la liste
      if (previousReaders) {
        queryClient.setQueryData<StripeTerminalReader[]>(
          ["stripe-readers"],
          (old) => {
            if (!old) return old
            return old.filter((reader) => reader.id !== readerId)
          }
        )
      }

      // Retourner le contexte pour le rollback
      return { previousReaders }
    },
    onError: (_error, _readerId, context) => {
      // Rollback en cas d'erreur
      if (context?.previousReaders) {
        queryClient.setQueryData(["stripe-readers"], context.previousReaders)
      }

      addToast({
        type: "error",
        title: "Erreur de suppression",
        description: "Impossible de supprimer le terminal",
      })
    },
    onSettled: () => {
      // Refetch pour s'assurer que les données sont à jour
      queryClient.invalidateQueries({ queryKey: ["stripe-readers"] })
    },
  })
}

/**
 * Hook pour ajouter un nouveau reader
 * Utilisé dans l'interface de gestion des terminaux
 */
export const useAddReaderMutation = () => {
  const { addToast } = useToast()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: StripeTerminalReaderCreateBeautyInput) => {
      const token = await getToken()
      if (!token) throw new Error("No token")

      const response = await customAxios.post(
        "/stripe-terminal/readers",
        data,
        {
          headers: { Authorization: token },
        }
      )

      return response.data
    },
    onMutate: async () => {
      // Annuler les requêtes en cours pour éviter les conflits
      await queryClient.cancelQueries({ queryKey: ["stripe-readers"] })

      // Snapshot de l'état précédent
      const previousReaders = queryClient.getQueryData<StripeTerminalReader[]>([
        "stripe-readers",
      ])

      // Retourner le contexte pour le rollback
      return { previousReaders }
    },
    onError: (error: unknown, _data, context) => {
      // Rollback en cas d'erreur
      if (context?.previousReaders) {
        queryClient.setQueryData(["stripe-readers"], context.previousReaders)
      }

      let errorMessage = "Erreur lors de l'enregistrement du terminal"
      if (error && typeof error === "object" && "response" in error) {
        const axiosError = error as {
          response?: { data?: { error?: string; stripe_error_code?: string } }
        }
        if (axiosError.response?.data?.error) {
          errorMessage = axiosError.response.data.error
        } else if (axiosError.response?.data?.stripe_error_code) {
          switch (axiosError.response.data.stripe_error_code) {
            case "resource_missing":
              errorMessage =
                "Code d'enregistrement invalide, déjà utilisé ou expiré. Essayez de redémarrer le terminal pour obtenir un nouveau code."
              break
            case "terminal_reader_invalid_location_type":
              errorMessage = "Type de location invalide pour ce terminal"
              break
            default:
              errorMessage = axiosError.response.data.error || errorMessage
          }
        }
      }

      addToast({
        type: "error",
        title: "Erreur d'enregistrement",
        description: errorMessage,
      })
    },
    onSettled: () => {
      // Refetch pour s'assurer que les données sont à jour
      queryClient.invalidateQueries({ queryKey: ["stripe-readers"] })
    },
  })
}

/**
 * Hook pour mettre à jour le nom d'un reader
 * Utilisé dans l'interface de gestion des terminaux
 */
export const useUpdateReaderMutation = () => {
  const { addToast } = useToast()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: { readerId: string; name: string }) => {
      const token = await getToken()
      if (!token) throw new Error("No token")

      const response = await customAxios.put(
        `/stripe-terminal/readers/${data.readerId}?name=${encodeURIComponent(
          data.name
        )}`,
        {},
        {
          headers: { Authorization: token },
        }
      )

      return response.data
    },
    onMutate: async (data) => {
      // Annuler les requêtes en cours pour éviter les conflits
      await queryClient.cancelQueries({ queryKey: ["stripe-readers"] })

      // Snapshot de l'état précédent
      const previousReaders = queryClient.getQueryData<StripeTerminalReader[]>([
        "stripe-readers",
      ])

      // Optimistic update
      if (previousReaders) {
        queryClient.setQueryData<StripeTerminalReader[]>(
          ["stripe-readers"],
          (old) => {
            if (!old) return old
            return old.map((reader) =>
              reader.id === data.readerId
                ? { ...reader, label: data.name }
                : reader
            )
          }
        )
      }

      // Retourner le contexte pour le rollback
      return { previousReaders }
    },
    onError: (_error, _data, context) => {
      // Rollback en cas d'erreur
      if (context?.previousReaders) {
        queryClient.setQueryData(["stripe-readers"], context.previousReaders)
      }

      addToast({
        type: "error",
        title: "Erreur de mise à jour",
        description: "Impossible de mettre à jour le nom du terminal",
      })
    },
    onSettled: () => {
      // Refetch pour s'assurer que les données sont à jour
      queryClient.invalidateQueries({ queryKey: ["stripe-readers"] })
    },
  })
}

// ============================================================================
// HOOKS DE CONFIGURATION (À déplacer vers interface d'admin)
// ============================================================================
// TODO: Déplacer ces hooks vers une interface d'administration séparée
// Car la configuration des locations et readers se fait une seule fois

/*
export const useCreateLocationMutation = () => {
  // À déplacer vers employerweb ou interface d'admin
}

export const useRegisterReaderMutation = () => {
  // À déplacer vers employerweb ou interface d'admin  
}

export const useStripeLocationsQuery = () => {
  // À déplacer vers employerweb ou interface d'admin
}
*/
